import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import {FormattedMessage} from 'react-intl';

import {ENTITY_TYPE_GEO, ENTITY_TYPE_VENUE} from '@stubhub/entity-helper';
import CloudinaryMedia, {helper} from '@stubhub/react-cloudinary';
import {connect} from '@stubhub/react-store-provider';
import {theme} from '@stubhub/react-theme-provider';

import {
  themeFontColor,
  themePrimaryColorBigText,
  themeBackgroundColor,
  themePartnerTextColor,
  headerMwebBackgroundGradientColor,
  headerBackgroundGradientColor,
} from '../../helpers/theming-helper';
import BreadCrumbs from '../react-breadcrumbs';
import UIButton from '../react-uikit-button';

import controller from './controller';
import DefaultLayout from './jumbotron-default-layout.js';
import FeaturedImgLayout from './jumbotron-featured-img-layout.js';
import LandingLayout from './jumbotron-landing-layout.js';
import SearchJumbotronLayout from './jumbotron-search-layout.js';
import messages from './messages';

import './index.scss';

const DEFAULT_TIMEOUT = 500;

@connect(controller)
class JumbotronBanner extends React.Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  redirectToEntityorVenuePage(counter, arrayItem) {
    /* istanbul ignore next */
    setTimeout(() => {
      if (__CLIENT__ && window) {
        window.location = `${window.location.origin}/${arrayItem.webURI}`;
      }
    }, DEFAULT_TIMEOUT);
  }

  updateSearchSuggestion = (props) => {
    const {point, suggestVariant = 'SUGGEST_VARIANT_7', getSearchSuggestions, searchSuggestions} = props;

    const getSearchSuggestionsOptions = {
      term: props.entity.name,
      point,
      suggestVariant,
      searchSuggestions,
    };

    /**
     * Trigger the Search Suggestion API Call with New Parameters
     *
     */
    getSearchSuggestions(getSearchSuggestionsOptions);
  };

  UNSAFE_componentWillMount() {
    const {entity, headerData} = this.props;
    if (entity && headerData.jumbotronType === 'search') {
      this.updateSearchSuggestion(this.props);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {entity} = this.props;
    if (entity.name !== nextProps.entity.name && nextProps.headerData.jumbotronType === 'search') {
      this.updateSearchSuggestion(nextProps);
    }
  }

  getDomainName() {
    const {globalRegistry} = this.context;
    const webTLD = globalRegistry.getDefaultWebTLD();

    return `https://www.stubhub.${webTLD}`;
  }

  render() {
    const {headerData, theme, themeProvider, entity, deviceType, searchSuggestions} = this.props;
    const {
      image = {},
      heading,
      nestedSubHeading,
      partners = {},
      backgroundLogo,
      backgroundColor,
      jumbotronType = 'default',
      breadcrumbConfig,
    } = headerData;
    const {partnerLogos, partnerText} = partners;
    let backgroundBigTextClassName = 'JumbotronBanner__backgroundBigText';
    let jumbotronContainerClassName = 'JumbotronBanner__container';
    const jumbotronHeadingClassName = 'JumbotronBanner__heading';
    const jumbotronSubHeadingClassName = 'JumbotronBanner__subHeading';
    const jumbotronEntityImageClass = 'JumbotronBanner__entityImage';
    let jumbotronEntityClassName = 'JumbotronBanner__entityName';
    const jumbotronEntityInfoClassName = 'JumbotronBanner__entityInfo';
    const {dynamicAttributes, images, entityType} = entity || {};
    let button;

    let featuredImgStyling = {};
    let featuredImg;
    let cityDescAttribute;
    let cityImageAttribute;

    let themePartnerBackgroundColor;
    let themePartnerTextColorClassName;

    if (searchSuggestions) {
      const topThreeSuggestion = searchSuggestions.slice(0, 3);
      button = topThreeSuggestion.map((arrayItem, counter) => (
        <div
          className={`Jumbotron--search slot-${counter}`}
          key={counter}
          onClick={this.redirectToEntityorVenuePage.bind(this, counter, arrayItem)}
        >
          <UIButton text={arrayItem.name} tertiary size="tiny" flow="main" />
        </div>
      ));
    }

    /**
     * Extend Header Text If No Partner Logos as Passed
     *
     */
    if (!partnerLogos && !partnerText) {
      jumbotronEntityClassName += ' extendText';
    }

    /**
     * Theme Font Color Override
     *
     */
    const themeFontColorClassName = themeFontColor(theme, themeProvider);

    /**
     * Nested Subheading Section
     *
     */
    const subHeading =
      nestedSubHeading && nestedSubHeading.length ? (
        <span
          key={nestedSubHeading}
          className={cs(jumbotronHeadingClassName, jumbotronSubHeadingClassName, themeFontColorClassName)}
        >
          {nestedSubHeading}
        </span>
      ) : null;

    let headingText = heading;
    if (entityType === ENTITY_TYPE_GEO) {
      headingText = <FormattedMessage {...messages.GEOGRAPHY} values={{heading}} />;
    } else if (entityType === ENTITY_TYPE_VENUE) {
      headingText = <FormattedMessage {...messages.VENUE} values={{heading}} />;
    }
    const nestedHeadingEl = [
      <h1 key="heading" className={cs(jumbotronHeadingClassName, themeFontColorClassName)}>
        {headingText}
      </h1>,
      subHeading,
    ];

    const headingEl = (
      <div className={jumbotronEntityClassName}>
        <div className="JumbotronBanner__favorites" />
        {nestedHeadingEl}
      </div>
    );

    /**
     * Theme BackgroundColor Override
     *
     */
    if (theme) {
      backgroundBigTextClassName += ` ${themePrimaryColorBigText(theme, themeProvider)}`;
      jumbotronContainerClassName +=
        deviceType === 'phone'
          ? ` ${headerMwebBackgroundGradientColor(backgroundColor, themeProvider)}`
          : ` ${headerBackgroundGradientColor(backgroundColor, themeProvider)}`;

      /**
       * Theme Fonts Color Overrides - Mweb only
       *
       */
      themePartnerBackgroundColor = ` ${themeBackgroundColor(theme, themeProvider)}`;
      themePartnerTextColorClassName = ` ${themePartnerTextColor(theme, themeProvider)}`;
    }

    /**
     * Header Primary Image Section
     *
     */
    const {source} = image;
    let entityHeaderImageTransforms = {};

    if (backgroundColor) {
      entityHeaderImageTransforms = {
        enableCloudinary: true,
        defaultImg: 'defaultSpacer.jpg',
        width: '220',
        height: '220',
      };
    }

    /**
     * Header Entity Logo Section
     *
     */
    const entityImageSection = backgroundLogo ? (
      <div className="JumbotronBanner__EntityImageContainer">
        <CloudinaryMedia
          className={jumbotronEntityImageClass}
          src={backgroundLogo}
          useNamedTransforms={false}
          alt={source}
          {...entityHeaderImageTransforms}
        />
      </div>
    ) : null;

    /**
     * Header Partner Designation Logo Section
     *
     */
    const partnerLogoImageTransforms = {
      enableCloudinary: true,
      defaultImg: 'defaultSpacer.jpg',
      width: '42',
      height: '27',
      useNamedTransforms: false,
      crop: 'scale',
    };
    const partnerLogoSection =
      partnerLogos || partnerText ? (
        <div className={cs('JumbotronBanner__imagesPartners', themePartnerBackgroundColor)}>
          {partnerLogos && (
            <div className="JumbotronBanner__logos">
              {partnerLogos.map((logo, index) => {
                return [
                  index === 1 ? <div key={index + logo} className="logoDivider" /> : null,
                  <CloudinaryMedia
                    key={logo + index}
                    className="JumbotronBanner__logo"
                    src={logo}
                    alt={`partner-logo-${index}`}
                    {...partnerLogoImageTransforms}
                  />,
                ];
              })}
            </div>
          )}
          <div className={cs('JumbotronBanner__partnerText', themePartnerTextColorClassName)}>{partnerText}</div>
        </div>
      ) : null;
    const partnerDesignation = (
      <div className="JumbotronBanner__partnerSection">
        {/* Header Partner Logo Section */}
        {partnerLogoSection}
      </div>
    );

    /*
     * SEO Breadcrumb links, core functionality to all layouts
     */
    const lastBreadcrumbData = {id: 'last-breadcrumb', name: entity.name, cleanName: entity.cleanName};
    const breadcrumbs =
      breadcrumbConfig && breadcrumbConfig.breadcrumbData ? (
        <BreadCrumbs
          className="JumbotronBanner__heading--breadcrumbs"
          breadcrumbs={breadcrumbConfig.breadcrumbData}
          customerBreadcrumbDatas={[lastBreadcrumbData]}
          showHome={breadcrumbConfig.showHome}
          useCleanName={breadcrumbConfig.useCleanName}
          entityWebURI={entity.webURI}
          fqdn={this.getDomainName()}
        />
      ) : null;

    let backgroundBigText =
      entity && (jumbotronType === 'default' || jumbotronType === 'search' || jumbotronType === 'landing') ? (
        <div className={backgroundBigTextClassName} aria-hidden="true">
          {entity.name?.toUpperCase()}
        </div>
      ) : null;

    /*
     * Featured Image Jumbotron specific logic and variable initializations
     */
    if (jumbotronType === 'featuredImg') {
      const {bannerImagery} = theme;
      const featuredImgSrc = helper.getCloudinaryImageURL({
        src: bannerImagery,
        width: 1250,
        height: 220,
        useNamedTransforms: false,
      });
      featuredImgStyling = {
        backgroundImage: `linear-gradient(180deg,transparent,rgba(0,0,0,.8)), url(${featuredImgSrc})`,
      };
      featuredImg = <div className="JumbotronBanner__background-featured-image" style={featuredImgStyling} />;
    }

    /*
     * Landing Jumbotron specific logic and variable initializations
     */
    if (jumbotronType === 'landing') {
      const cityHeaderAttr = dynamicAttributes && dynamicAttributes.find((item) => item.name === 'header_copy');
      if (cityHeaderAttr) {
        cityDescAttribute = cityHeaderAttr.value;
      }

      if (images && images.length > 0) {
        const [cityImages] = images.filter((item) => item && item.primaryInd);
        cityImageAttribute = cityImages ? cityImages.cloudinaryPublicId : (images[0] || {}).cloudinaryPublicId;
        featuredImgStyling = {
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8)), url(${helper.getCloudinaryImageURL(
            {
              publicIdSrc: cityImageAttribute,
              useNamedTransforms: false,
            }
          )})`,
        };
        backgroundBigText = null;
      }
      featuredImg = <div className="JumbotronBanner__background-featured-image" style={featuredImgStyling} />;
    }

    const layoutProps = {
      jumbotronType,
      backgroundBigText,
      jumbotronContainerClassName,
      jumbotronEntityInfoClassName,
      headingEl,
      breadcrumbs,
      entityImageSection,
      partnerDesignation,
      featuredImg,
      cityDescAttribute,
      button,
      searchSuggestions,
    };

    let JumbotronLayout = DefaultLayout;

    if (jumbotronType === 'featuredImg') {
      JumbotronLayout = FeaturedImgLayout;
    } else if (jumbotronType === 'landing') {
      JumbotronLayout = LandingLayout;
    } else if (jumbotronType === 'search') {
      JumbotronLayout = SearchJumbotronLayout;
    }

    return <JumbotronLayout {...layoutProps} />;
  }
}

JumbotronBanner.propTypes = {
  headerData: PropTypes.object.isRequired,

  /** Current Global theme object */
  theme: PropTypes.object,

  /** ThemeProvider object for applying the theme */
  themeProvider: PropTypes.func.isRequired,
  entity: PropTypes.object.isRequired,
  screenSize: PropTypes.string,

  /**  Type of device */
  deviceType: PropTypes.string,
  searchSuggestions: PropTypes.array,
};

export const JumbotronBannerTest = JumbotronBanner;

export default theme(JumbotronBanner);
